import { html, PropertyValues } from "lit";
import { customElement, state } from "lit/decorators.js";
import { Api, ChatContextItem, PublicUser } from "../api";
import { AudioPlayer, AudioState } from "../components/audio-player";
import "../components/chat-view";
import { PreviewContext } from "../components/preview";
import { SearchResult } from "../server/media";
import { aiIcon } from "../utils/icons";
import { BaseElement } from "../utils/ui-components";
import { ChatState } from "./chat-state";
import "../components/index";
import { router } from "../utils/routing";

const chatState = ChatState.getInstance();

@customElement("search-page")
export class SearchPage extends BaseElement {
    @state()
    loading = true;

    @state()
    error?: string;

    @state()
    searchResult: SearchResult = { hits: [], pathFacets: [], total: 0 };

    @state()
    startDate = new Date();

    @state()
    endDate = new Date();

    @state()
    query = "";

    @state()
    queryError = false;

    @state()
    private currentPage = 0;

    @state()
    private hasMore = true;

    @state()
    private sources: Array<{
        sourcePath: string;
        shortName?: string;
        displayName: string;
        paths: Array<{ path: string; count: number }>;
    }> = [];

    @state()
    private totalDocuments = 0;

    @state()
    private selectedPaths: Set<string> = new Set();

    @state()
    private sidebarOpen = false;

    @state()
    private chatOpen = false;

    @state()
    private user?: PublicUser;

    @state()
    private previewContext: PreviewContext | null = null;

    @state()
    private showAlert = false;

    @state()
    private alertMessage = "";

    private unsubscribe?: () => void;

    private handleAudioRequest = (state: AudioState) => {
        const player = this.renderRoot.querySelector("audio-player") as AudioPlayer;
        player?.play(state);
    };

    async search(loadMore = false) {
        if (!loadMore) {
            this.currentPage = 0;
            this.hasMore = true;
        }

        if (!this.hasMore) return;

        this.loading = true;
        try {
            // Process dates for API call without modifying state
            const dateFrom = new Date(this.startDate);
            dateFrom.setHours(0, 0, 0, 0);

            const dateTo = new Date(this.endDate);
            dateTo.setHours(23, 59, 59, 999);

            // Local function to minimize paths for API call
            const getMinimalPaths = (paths: Set<string>): string[] => {
                const result = new Set<string>();

                for (const source of this.sources) {
                    const sourcePaths = new Set(source.paths.map((p) => p.path));
                    const selectedSourcePaths = Array.from(paths).filter((p) => sourcePaths.has(p));

                    if (selectedSourcePaths.length === source.paths.length) {
                        result.add(source.sourcePath);
                    } else {
                        selectedSourcePaths.forEach((p) => result.add(p));
                    }
                }

                return Array.from(result);
            };

            const minimizedPaths = this.selectedPaths.size > 0 ? getMinimalPaths(this.selectedPaths) : undefined;

            const result = await Api.search({
                query: this.query,
                dateFrom,
                dateTo,
                pageSize: 10,
                page: this.currentPage,
                sort: "desc",
                paths: minimizedPaths,
            });

            if (loadMore) {
                this.searchResult = {
                    ...result,
                    hits: [...this.searchResult.hits, ...result.hits],
                };
            } else {
                this.searchResult = result;
            }

            this.hasMore = result.hits.length == 10;
            if (this.hasMore) this.currentPage++;
        } catch (e) {
            if (!loadMore) {
                this.searchResult = { hits: [], pathFacets: [], total: 0 };
            }
        } finally {
            this.loading = false;
        }
    }

    protected async firstUpdated(_changedProperties: PropertyValues): Promise<void> {
        super.firstUpdated(_changedProperties);

        // Check authentication and get user info
        try {
            this.user = await Api.me();
        } catch (e) {
            location.href = "/";
            return;
        }

        // Get oldest media date
        try {
            const oldestResult = await Api.search({
                pageSize: 1,
                sort: "asc",
            });
            if (oldestResult.hits.length > 0) {
                this.startDate = new Date(oldestResult.hits[0].item.date);
            } else {
                // Fallback to 30 days ago if no results
                this.startDate = new Date();
                this.startDate.setDate(this.endDate.getDate() - 30);
            }
        } catch (e) {
            // Fallback to 30 days ago if search fails
            this.startDate = new Date();
            this.startDate.setDate(this.endDate.getDate() - 30);
        }

        this.unsubscribe = chatState.subscribe(() => {
            this.requestUpdate();
        });

        Promise.all([this.search(), this.fetchSources()]);
    }

    private handleAddToContext = async (e: CustomEvent<ChatContextItem>) => {
        const success = await chatState.addContextItem(e.detail);
        if (!success) {
            this.alertMessage =
                "This item is too large to add to the conversation. The AI Assistant can only handle a limited amount of content at once.";
            this.showAlert = true;
        }
    };

    private addAllToContext = async () => {
        const items: ChatContextItem[] = [];

        for (const hit of this.searchResult.hits) {
            if (hit.matches?.some((m) => m.type == "segment" || m.type == "subtitle")) {
                const segmentIndices = new Set(hit.matches.filter((m) => m.type == "segment" || m.type == "subtitle").map((m) => m.segmentIndex));

                for (const index of segmentIndices) {
                    const segment = hit.item.segments?.[index];
                    if (segment) {
                        items.push({
                            type: "segment",
                            item: hit.item,
                            segment: segment,
                        });
                    }
                }
            } else {
                items.push({
                    type: "item",
                    item: hit.item,
                });
            }
        }

        const addedCount = await chatState.addContextItems(items);

        if (addedCount < items.length) {
            this.alertMessage = `Only ${addedCount} out of ${items.length} items could be added. The AI Assistant can only handle a limited amount of content at once.`;
            this.showAlert = true;
        }
    };

    private onSearch = (query: string) => {
        this.query = query;
        this.search();
    };

    private onDateChange = (startDate: Date, endDate: Date) => {
        this.startDate = startDate;
        this.endDate = endDate;
        this.search();
    };

    private onLoadMore = () => {
        this.search(true);
    };

    private handleShowPreview = (context: PreviewContext) => {
        this.previewContext = context;
        router.pushModal(() => (this.previewContext = null));
    };

    private handleOpenSidebar = () => {
        this.sidebarOpen = true;
        router.pushModal(() => (this.sidebarOpen = false));
    };

    private handleOpenChat = () => {
        this.chatOpen = true;
        router.pushModal(() => (this.chatOpen = false));
    };

    render() {
        return html`
            <div class="h-screen flex flex-col bg-gray-200">
                <top-bar .user=${this.user}></top-bar>

                <!-- Main content area -->
                <div class="flex h-screen pt-12 pb-12 lg:pb-0">
                    <!-- Sidebar - fixed position adjusted -->
                    <div
                        class="${this.sidebarOpen ? "translate-x-0" : "-translate-x-full"}
                        lg:translate-x-0 lg:w-64 fixed lg:static top-0 lg:top-12 bottom-0 left-0 w-full
                        transform transition-transform duration-200 ease-in-out z-40"
                    >
                        <source-sidebar
                            .sources=${this.sources}
                            .totalDocuments=${this.totalDocuments}
                            .selectedPaths=${this.selectedPaths}
                            @pathsChanged=${(e: CustomEvent) => {
                                this.selectedPaths = new Set(e.detail.paths);
                                this.search();
                            }}
                            .onCloseSidebar=${() => (this.sidebarOpen = false)}
                        ></source-sidebar>
                    </div>

                    <!-- Backdrop for mobile sidebar -->
                    ${this.sidebarOpen
                        ? html`
                              <div
                                  class="fixed inset-0 bg-black/20 backdrop-blur-sm lg:hidden z-30"
                                  @click=${(e: MouseEvent) => {
                                      if (e.target === e.currentTarget) {
                                          this.sidebarOpen = false;
                                          router.pop();
                                      }
                                  }}
                              ></div>
                          `
                        : ""}

                    <!-- Main content container -->
                    <div class="flex flex-1">
                        <div class="flex w-full">
                            <!-- Search section -->
                            <div class="flex-1 min-w-0 h-[calc(100vh-3rem)] lg:w-1/2 overflow-hidden">
                                <search-view
                                    class="h-full pb-24 lg:pb-0"
                                    .searchResult=${this.searchResult}
                                    .loading=${this.loading}
                                    .query=${this.query}
                                    .startDate=${this.startDate}
                                    .endDate=${this.endDate}
                                    .hasMore=${this.hasMore}
                                    .onSearch=${this.onSearch}
                                    .onDateChange=${this.onDateChange}
                                    .onLoadMore=${this.onLoadMore}
                                    .onAudioRequest=${this.handleAudioRequest}
                                    @add-to-context=${this.handleAddToContext}
                                    .onAddAllToContext=${this.addAllToContext}
                                ></search-view>

                                ${this.loading && this.currentPage > 0
                                    ? html`
                                          <div class="fixed bottom-12 left-0 right-0 flex justify-center lg:static lg:mt-4">
                                              <div class="bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-2 shadow-lg">
                                                  <div class="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                                  <span class="text-sm text-gray-700">Loading more results...</span>
                                              </div>
                                          </div>
                                      `
                                    : null}
                            </div>

                            <!-- Chat section - fixed position adjusted -->
                            <div
                                class="${this.chatOpen ? "translate-x-0" : "translate-x-full"}
                                lg:translate-x-0 fixed lg:static top-0 lg:top-12 bottom-0 right-0 w-full lg:w-1/2
                                transform transition-transform duration-200 ease-in-out z-40 bg-white lg:bg-transparent"
                            >
                                <chat-view
                                    .showHeader=${true}
                                    .onAudioRequest=${this.handleAudioRequest}
                                    .onClose=${() => {
                                        this.chatOpen = false;
                                        router.pop();
                                    }}
                                    .onShowPreview=${this.handleShowPreview}
                                ></chat-view>
                            </div>

                            <!-- Backdrop for mobile chat -->
                            ${this.chatOpen
                                ? html`
                                      <div
                                          class="fixed inset-0 bg-black/20 backdrop-blur-sm lg:hidden z-30"
                                          @click=${() => {
                                              this.chatOpen = false;
                                              router.pop();
                                          }}
                                      ></div>
                                  `
                                : ""}
                        </div>
                    </div>
                </div>

                <!-- Mobile bottom navigation -->
                <div
                    class="lg:hidden fixed bottom-0 left-0 right-0 h-12 bg-white border-t border-gray-200 flex items-center justify-around px-4 z-30"
                >
                    <button @click=${this.handleOpenSidebar} class="flex flex-col items-center gap-0.5 p-1 text-gray-600 hover:text-gray-900">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                        <span class="text-[10px] font-medium">Sources</span>
                    </button>

                    <button
                        @click=${this.handleOpenChat}
                        class="flex flex-col items-center gap-0.5 p-1
                        ${chatState.session?.context?.length > 0
                            ? "text-blue-600 hover:text-blue-700 animate-pulse-subtle"
                            : "text-gray-600 hover:text-gray-900"}
                        transition-colors duration-200"
                    >
                        <i class="w-5 h-5 icon">${aiIcon}</i>
                        <span class="text-[10px] font-medium">AI Assistant</span>
                    </button>
                </div>

                <audio-player class="z-[60]"></audio-player>

                ${this.previewContext
                    ? html`
                          <preview-overlay
                              .previewContext=${this.previewContext}
                              .onAudioRequest=${this.handleAudioRequest}
                              .onClose=${() => {
                                  this.previewContext = null;
                                  router.pop();
                              }}
                          ></preview-overlay>
                      `
                    : null}
                ${this.showAlert ? html` <alert-modal .message=${this.alertMessage} .onClose=${() => (this.showAlert = false)}></alert-modal>` : null}
            </div>
        `;
    }

    private async fetchSources() {
        try {
            const response = await Api.getSources();
            this.sources = response.sources.map((source) => ({
                ...source,
                paths: source.paths.sort((a, b) => {
                    const titleA = a.path.replace(source.sourcePath, "").replace(/^-/, "");
                    const titleB = b.path.replace(source.sourcePath, "").replace(/^-/, "");
                    return titleA.localeCompare(titleB);
                }),
            }));
            this.totalDocuments = response.totalDocuments;
        } catch (e) {
            console.error("Failed to fetch sources:", e);
        }
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        if (this.unsubscribe) {
            this.unsubscribe();
        }
    }
}
